import { ReactNode, useId } from 'react';

/**
 * DonutChart — categorical breakdown ring with an inline legend.
 *
 * Ported from the legacy V1 SPA's hand-rolled `asDonut()` dashboard
 * visualisation (public/index.html) so the V1→V2 cutover loses no charts.
 * The V2 app ships no charting dependency and hand-rolls its SVG marks; this
 * follows that established pattern rather than pulling in a chart library for
 * two donuts (keeps the bundle lean and the build dependency-free).
 *
 * Design:
 *   - M3 Card (outlined) chrome, matching StatCard's surface/border/radius.
 *   - 8dp spacing grid throughout (--sp-* tokens).
 *   - Distinct hues per segment (caller-supplied) — legible, brand-aligned.
 *   - Loading / empty states per Material behavioral standards.
 *   - Accessible: role="img" + aria-label enumerating the breakdown; the
 *     legend repeats every value as text so the chart is never color-only.
 *
 * Parity lane (V1→V2), Domain 01 charts.
 */

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export interface DonutChartProps {
  /** Card title, e.g. "Tax bill status". */
  title: string;
  /** Small caption under the title, e.g. "All bills · current org". */
  caption?: string;
  segments: DonutSegment[];
  /** Word shown under the total in the ring center, e.g. "Bills". */
  centerLabel?: string;
  /** Pill label at top-right; defaults to `${total} total`. */
  countPill?: ReactNode;
  loading?: boolean;
  /** Message when there is no data to plot. */
  emptyMessage?: string;
  /** Ring diameter in px. */
  size?: number;
  /** Ring stroke width in px. */
  stroke?: number;
  /**
   * Opt-in: makes each segment (its legend row AND its ring arc) an activatable
   * control that calls back with the clicked segment + its index in `segments`.
   * When omitted the chart is purely presentational (its default). Keyboard
   * support is native — clickable legend rows render as real buttons.
   */
  onSegmentClick?: (segment: DonutSegment, index: number) => void;
}

export function DonutChart({
  title,
  caption,
  segments,
  centerLabel = 'Total',
  countPill,
  loading = false,
  emptyMessage = 'No data yet.',
  size = 132,
  stroke = 20,
  onSegmentClick,
}: DonutChartProps) {
  const titleId = useId();
  const plotted = segments.filter((s) => s.value > 0);
  const total = segments.reduce((a, s) => a + s.value, 0);

  const ariaLabel =
    total > 0
      ? `${title}: ${plotted.map((s) => `${s.label} ${s.value}`).join(', ')}`
      : `${title}: no data`;

  return (
    <section
      aria-labelledby={titleId}
      style={{
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-lg)',
        boxShadow: 'var(--sh-sm)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-4)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <div>
          <h3
            id={titleId}
            style={{
              margin: 0,
              fontSize: 'var(--fs-lg)',
              fontWeight: 'var(--fw-semibold)',
              color: 'var(--color-ink)',
            }}
          >
            {title}
          </h3>
          {caption && (
            <p style={{ margin: 'var(--sp-1) 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
              {caption}
            </p>
          )}
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: 'var(--fs-xs)',
            fontWeight: 'var(--fw-medium)',
            color: 'var(--color-ink-2)',
            background: 'var(--color-status-gray-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-full)',
            padding: '2px 10px',
            whiteSpace: 'nowrap',
          }}
        >
          {countPill ?? `${total} total`}
        </span>
      </header>

      <div style={{ padding: 'var(--sp-5)' }}>
        {loading ? (
          <DonutSkeleton size={size} stroke={stroke} />
        ) : total === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6) var(--sp-4)',
              textAlign: 'center',
              color: 'var(--color-muted)',
              fontSize: 'var(--fs-md)',
            }}
          >
            {emptyMessage}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'var(--sp-6)',
            }}
          >
            <DonutRing
              segments={plotted}
              total={total}
              centerLabel={centerLabel}
              size={size}
              stroke={stroke}
              ariaLabel={ariaLabel}
              onArcClick={onSegmentClick ? (seg) => onSegmentClick(seg, segments.indexOf(seg)) : undefined}
            />
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
                flex: '1 1 160px',
                minWidth: 0,
              }}
            >
              {segments.map((seg, i) => (
                <li key={seg.label}>
                  <LegendRow
                    segment={seg}
                    onClick={onSegmentClick ? () => onSegmentClick(seg, i) : undefined}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * One legend entry: the color swatch, label, and value. When `onClick` is
 * supplied the whole row is a real <button> (native keyboard + focus), and the
 * label takes the repo's clickable-text affordance (brand color + underline) so
 * it reads as navigable; otherwise it is a plain, static row.
 */
function LegendRow({ segment, onClick }: { segment: DonutSegment; onClick?: () => void }) {
  const swatch = (
    <span
      aria-hidden="true"
      style={{ width: 10, height: 10, borderRadius: 'var(--r-sm)', background: segment.color, flexShrink: 0 }}
    />
  );
  const value = (
    <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)', fontVariantNumeric: 'tabular-nums' }}>
      {segment.value}
    </span>
  );
  if (!onClick) {
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-md)', color: 'var(--color-ink-2)' }}>
        {swatch}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{segment.label}</span>
        {value}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={`View ${segment.label} parcels`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        width: '100%',
        padding: 'var(--sp-1) var(--sp-2)',
        margin: 0,
        background: 'none',
        border: 'none',
        borderRadius: 'var(--r-sm)',
        font: 'inherit',
        fontSize: 'var(--fs-md)',
        color: 'var(--color-ink-2)',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      {swatch}
      <span style={{
        flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        color: 'var(--brand-primary)', textDecoration: 'underline',
      }}>
        {segment.label}
      </span>
      {value}
    </button>
  );
}

function DonutRing({
  segments,
  total,
  centerLabel,
  size,
  stroke,
  ariaLabel,
  onArcClick,
}: {
  segments: DonutSegment[];
  total: number;
  centerLabel: string;
  size: number;
  stroke: number;
  ariaLabel: string;
  onArcClick?: (segment: DonutSegment) => void;
}) {
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;

  let offsetAcc = 0;
  const arcs = segments.map((seg) => {
    const frac = seg.value / total;
    const dash = frac * circumference;
    const arc = (
      <circle
        key={seg.label}
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke={seg.color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${circumference - dash}`}
        strokeDashoffset={-offsetAcc}
        onClick={onArcClick ? () => onArcClick(seg) : undefined}
        style={onArcClick ? { cursor: 'pointer' } : undefined}
      />
    );
    offsetAcc += dash;
    return arc;
  });

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flexShrink: 0 }}
    >
      {/* Track */}
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="var(--color-status-gray-bg)" strokeWidth={stroke} />
      {/* Segments — rotated so the ring starts at 12 o'clock. */}
      <g transform={`rotate(-90 ${cx} ${cy})`}>{arcs}</g>
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        style={{ fontSize: 'var(--fs-2xl)', fontWeight: 'var(--fw-bold)', fill: 'var(--color-ink)' }}
      >
        {total}
      </text>
      <text
        x={cx}
        y={cy + 16}
        textAnchor="middle"
        style={{ fontSize: 'var(--fs-xs)', fill: 'var(--color-ink-3)', letterSpacing: 'var(--ls-wide)' }}
      >
        {centerLabel.toUpperCase()}
      </text>
    </svg>
  );
}

function DonutSkeleton({ size, stroke }: { size: number; stroke: number }) {
  const radius = (size - stroke) / 2;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-6)' }} aria-hidden="true">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-status-gray-bg)"
          strokeWidth={stroke}
        />
      </svg>
      <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{ height: 12, borderRadius: 'var(--r-sm)', background: 'var(--color-status-gray-bg)' }}
          />
        ))}
      </div>
    </div>
  );
}
